import { useEffect, useMemo, useRef, useState } from "react";
import { startOfWeek, barGeometry } from "./ganttUtils";

const WEEK_DAYS = 7;

function currentWeekHasBars(orders) {
  const ws = startOfWeek(new Date());
  const we = new Date(ws);
  we.setDate(we.getDate() + WEEK_DAYS);
  return orders.some((o) => barGeometry(o, ws, we));
}

function nearestScheduled(orders) {
  const open = orders.filter((o) => o.start_date && o.status !== "Completed");
  const pool = open.length ? open : orders.filter((o) => o.start_date);
  if (!pool.length) return null;
  const nowT = Date.now();
  return pool.reduce((a, b) => (Math.abs(new Date(a.start_date) - nowT) <= Math.abs(new Date(b.start_date) - nowT) ? a : b));
}

// Visible window (anchor + day count), navigation, and the one-time auto-jump to the nearest scheduled order.
export function useGanttWindow(orders) {
  const [anchor, setAnchor] = useState(startOfWeek(new Date()));
  const [days, setDays] = useState(WEEK_DAYS);
  const autoJumped = useRef(false);

  useEffect(() => {
    if (autoJumped.current || !orders.length) return;
    autoJumped.current = true;
    if (currentWeekHasBars(orders)) return;
    const nearest = nearestScheduled(orders);
    if (nearest) setAnchor(startOfWeek(new Date(nearest.start_date)));
  }, [orders, autoJumped, setAnchor]);

  const cols = useMemo(() => Array.from({ length: days }, (_, i) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  }), [anchor, days]);
  const winStart = useMemo(() => new Date(anchor), [anchor]);
  const winEnd = useMemo(() => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + days);
    return d;
  }, [anchor, days]);

  const move = (dir) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + dir * days);
    setAnchor(d);
  };
  const today = () => setAnchor(startOfWeek(new Date()));

  return { days, setDays, cols, winStart, winEnd, move, today };
}
